import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { fmtIDR, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FakturTable } from "@/components/faktur/FakturTable";
import { toast } from "sonner";
import { ReceiptText, ArrowDownLeft, ArrowUpRight, Scale, Ban } from "lucide-react";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

function Stat({ icon: Icon, label, value, sub, tone, tid }) {
  return (
    <Card className="p-4 space-y-1" data-testid={tid}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className={`h-4 w-4 ${tone}`} />{label}</div>
      <div className={`num text-lg font-semibold ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function Summary({ data }) {
  const m = data.masukan?.summary; const k = data.keluaran?.summary;
  if (!m || !k) return null;
  const pay = k.counted_ppn - m.counted_ppn;
  return (
    <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
      <Stat icon={ArrowUpRight} tone="text-sky-700" label="PPN Keluaran wajib disetor" value={fmtIDR(k.counted_ppn)} tid="faktur-sum-keluaran"
        sub={`${k.count} faktur · ${k.matched} cocok · ${k.unmatched} belum ada invoice`} />
      <Stat icon={ArrowDownLeft} tone="text-emerald-700" label="PPN Masukan dapat dikreditkan" value={fmtIDR(m.counted_ppn)} tid="faktur-sum-masukan"
        sub={`${m.count} faktur · ${m.matched} cocok · ${m.unmatched} belum ada transaksi`} />
      <Stat icon={Ban} tone="text-rose-600" label="PPN Masukan tidak dapat dikreditkan" value={fmtIDR(m.not_counted_ppn)} tid="faktur-sum-nokredit"
        sub={`${m.not_counted} faktur tidak dikreditkan · ${m.docs_without_faktur} transaksi ber-PPN tanpa faktur`} />
      <Stat icon={Scale} tone={pay >= 0 ? "text-amber-700" : "text-emerald-700"} label={pay >= 0 ? "PPN Kurang Bayar" : "PPN Lebih Bayar"}
        value={fmtIDR(Math.abs(pay))} tid="faktur-sum-payable" sub="Keluaran disetor − Masukan dikreditkan" />
    </div>
  );
}

export default function FakturPajak() {
  const { selectedClientId: clientId } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    const qs = `client_id=${clientId}&year=${year}${month !== "0" ? `&month=${month}` : ""}`;
    try {
      const [m, k] = await Promise.all([api.get(`/faktur?kind=masukan&${qs}`), api.get(`/faktur?kind=keluaran&${qs}`)]);
      setData({ masukan: m.data, keluaran: k.data });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setLoading(false);
  }, [clientId, year, month]);
  useEffect(() => { load(); }, [load]);
  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - i);
  if (!clientId) return <Card className="p-8 text-center text-muted-foreground" data-testid="faktur-no-client">Pilih klien terlebih dahulu di header.</Card>;
  return (
    <div className="space-y-5" data-testid="faktur-page">
      <div className="page-sticky flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl sm:text-3xl font-semibold flex items-center gap-2"><ReceiptText className="h-6 w-6 text-primary" />Rekap Faktur Pajak</h1>
          <p className="text-sm text-muted-foreground max-w-2xl mt-1">Impor ekspor faktur dari Coretax, sistem langsung mencocokkan dengan transaksi & invoice (NPWP + nilai PPN, atau nomor referensi) dan menentukan PPN yang dapat dikreditkan sesuai kode transaksi DJP.</p>
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1"><label className="text-xs text-muted-foreground">Masa</label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-36" data-testid="faktur-month"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="0">Setahun penuh</SelectItem>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1"><label className="text-xs text-muted-foreground">Tahun</label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-24" data-testid="faktur-year"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
      </div>
      <Summary data={data} />
      <Tabs defaultValue="masukan">
        <TabsList>
          <TabsTrigger value="masukan" data-testid="faktur-tab-masukan">Faktur Masukan ({data.masukan?.rows.length ?? 0})</TabsTrigger>
          <TabsTrigger value="keluaran" data-testid="faktur-tab-keluaran">Faktur Keluaran ({data.keluaran?.rows.length ?? 0})</TabsTrigger>
        </TabsList>
        {["masukan", "keluaran"].map((k) => (
          <TabsContent key={k} value={k}><FakturTable kind={k} clientId={clientId} data={data[k]} loading={loading} reload={load} period={`${year}${month !== "0" ? `_${month.padStart(2, "0")}` : ""}`} /></TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
